// ConsoleAdmin.tsx
// Main container for the Admin console page.
// Owns all state, data fetching, and handlers.
// Renders are delegated to section components for readability:
//   - UserManagementSection
//   - TableManagementSection
//   - DashboardManagementSection

import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import ConsoleLayout from '../ConsoleLayout';
import { useAuth } from '../../../context/AuthContext';
import { consoleUsersApi } from '../../../services/api/console/users.api';
import { consoleTablesApi } from '../../../services/api/console/tables.api';
import { consoleDashboardsApi } from '../../../services/api/console/dashboards.api';
import {
  ConsoleDashboardSummary,
  ConsoleManagedTable,
  ConsoleTableCatalogItem,
  ConsoleUser,
} from '../../../types';

import { ColumnFieldRow } from './types';
import UserManagementSection from './UserManagementSection';
import TableManagementSection from './TableManagementSection';
import DashboardManagementSection from './DashboardManagementSection';
import { useDashboardForm } from './useDashboardForm';

const ConsoleAdmin: React.FC = () => {
  const { user, refreshProfile } = useAuth();
  const requesterEmail = user?.email || user?.userId || '';

  // ── Data state ──────────────────────────────────────────────────────────────
  const [users, setUsers] = useState<ConsoleUser[]>([]);
  const [tableCatalog, setTableCatalog] = useState<ConsoleTableCatalogItem[]>([]);
  const [assignedTables, setAssignedTables] = useState<ConsoleManagedTable[]>([]);
  const [dashboards, setDashboards] = useState<ConsoleDashboardSummary[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTables, setLoadingTables] = useState(false);
  const [loadingDashboards, setLoadingDashboards] = useState(false);
  const [tableDeleteLoadingId, setTableDeleteLoadingId] = useState<number | null>(null);
  const [dashboardDeleteLoadingId, setDashboardDeleteLoadingId] = useState('');

  // ── User form state ──────────────────────────────────────────────────────────
  const [userEmail, setUserEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<'admin' | 'business_analyst' | 'tableau_admin'>('business_analyst');
  const [isActive, setIsActive] = useState(true);

  // ── Table form state ─────────────────────────────────────────────────────────
  const [selectedTableId, setSelectedTableId] = useState('');
  const [dailyRefresh, setDailyRefresh] = useState(true);
  const [primaryDeveloper, setPrimaryDeveloper] = useState('');
  const [secondaryDeveloper, setSecondaryDeveloper] = useState('');
  
  const [columnFields, setColumnFields] = useState<ColumnFieldRow[]>([{ field_key: '', field_label: '' }]);
  const [columnFieldTableId, setColumnFieldTableId] = useState('');

  // ── Dashboard form state ─────────────────────────────────────────────────────
  const dashboardForm = useDashboardForm(dashboards);

  // ── Derived/memoized values ──────────────────────────────────────────────────
  const sortedUsers = useMemo(
    () => [...users].sort((a, b) => a.user_email.localeCompare(b.user_email)),
    [users]
  );

  const activeAssignableUsers = useMemo(
    () => sortedUsers.filter((entry) => entry.is_active && (entry.user_role === 'admin' || entry.user_role === 'business_analyst' || entry.user_role === 'tableau_admin')),
    [sortedUsers]
  );

  // filters only active users with tableau_admin role — used for User Manager Admin dropdown in dashboard assignment
  // named dashboard-prefixed as tables may have same pattern later (tableUserManagerAdmin)
  const activeDashboardUserManagerAdminUsers = useMemo(
    () => sortedUsers.filter((entry) => entry.is_active && entry.user_role === 'tableau_admin'),
    [sortedUsers]
  );

  const selectedCatalog = useMemo(
    () => tableCatalog.find((row) => row.id === Number(selectedTableId)),
    [tableCatalog, selectedTableId]
  );

  const selectedColumnFieldTable = useMemo(
    () => assignedTables.find((row) => row.table_id === Number(columnFieldTableId)),
    [assignedTables, columnFieldTableId]
  );

  // ── Data fetching ────────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    if (!requesterEmail || !user?.isAdmin) return;
    setLoadingUsers(true);
    try {
      const list = await consoleUsersApi.listUsers(requesterEmail);
      setUsers(list);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  }, [requesterEmail, user?.isAdmin]);

  const fetchTableData = useCallback(async () => {
    if (!requesterEmail || !user?.isAdmin) return;
    setLoadingTables(true);
    try {
      const [catalog, tables] = await Promise.all([
        consoleTablesApi.getTableCatalog(requesterEmail),
        consoleTablesApi.getConfiguredTables(requesterEmail, true),
      ]);
      setTableCatalog(catalog);
      setAssignedTables(tables);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to load table metadata');
    } finally {
      setLoadingTables(false);
    }
  }, [requesterEmail, user?.isAdmin]);

  const fetchDashboards = useCallback(async () => {
    if (!requesterEmail || !user?.isAdmin) return;
    setLoadingDashboards(true);
    try {
      const rows = await consoleDashboardsApi.listDashboards(requesterEmail, true);
      setDashboards(rows);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to load dashboards');
    } finally {
      setLoadingDashboards(false);
    }
  }, [requesterEmail, user?.isAdmin]);

  useEffect(() => {
    void fetchUsers();
    void fetchTableData();
    void fetchDashboards();
  }, [fetchUsers, fetchTableData, fetchDashboards]);

  // Sync dailyRefresh checkbox when selected table changes
  useEffect(() => {
    if (!selectedCatalog) return;
    setDailyRefresh(!!selectedCatalog.daily_refresh);
  }, [selectedCatalog]);

  // Sync column fields editor when selected table changes
  useEffect(() => {
    if (!columnFieldTableId) {
      setColumnFields([{ field_key: '', field_label: '' }]);
      return;
    }
    const schema = selectedColumnFieldTable?.column_field_schema || [];
    if (!schema.length) {
      setColumnFields([{ field_key: '', field_label: '' }]);
      return;
    }
    setColumnFields(
      schema.map((item) => ({
        field_key: item.field_key || '',
        field_label: item.field_label || '',
      }))
    );
  }, [columnFieldTableId, selectedColumnFieldTable]);

  // Auto-select first assigned table for column fields editor
  useEffect(() => {
    if (!assignedTables.length) {
      setColumnFieldTableId('');
      return;
    }
    const exists = assignedTables.some((row) => String(row.table_id) === columnFieldTableId);
    if (!columnFieldTableId || !exists) {
      setColumnFieldTableId(String(assignedTables[0].table_id));
    }
  }, [assignedTables, columnFieldTableId]);

  // ── User handlers ────────────────────────────────────────────────────────────
  const handleCreateUser = async (event: FormEvent) => {
    event.preventDefault();
    if (!requesterEmail) {
      toast.error('Missing requester email');
      return;
    }
    try {
      await consoleUsersApi.createUser({
        requester_email: requesterEmail,
        user_email: userEmail.trim().toLowerCase(),
        user_first_name: firstName.trim(),
        user_last_name: lastName.trim(),
        user_role: role,
        is_active: isActive,
      });
      toast.success('User saved');
      setUserEmail('');
      setFirstName('');
      setLastName('');
      setRole('business_analyst');
      setIsActive(true);
      await fetchUsers();
      await refreshProfile();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleToggleUserStatus = async (target: ConsoleUser) => {
    if (!requesterEmail) return;
    try {
      await consoleUsersApi.setUserStatus({
        requester_email: requesterEmail,
        user_email: target.user_email,
        is_active: !target.is_active,
      });
      toast.success(`User ${!target.is_active ? 'activated' : 'deactivated'}`);
      await fetchUsers();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to update user status');
    }
  };

  // ── Table handlers ───────────────────────────────────────────────────────────
  const handleAssignTable = async (event: FormEvent) => {
    event.preventDefault();
    if (!requesterEmail || !selectedCatalog) {
      toast.error('Select a table first');
      return;
    }
    if (!primaryDeveloper || !secondaryDeveloper) {
      toast.error('Assign both primary and secondary users');
      return;
    }
    try {
      await consoleTablesApi.assignAdminTable({
        requester_email: requesterEmail,
        table_id: selectedCatalog.id,
        primary_developer_email: primaryDeveloper,
        secondary_developer_email: secondaryDeveloper,
        daily_refresh: dailyRefresh,
      });
      toast.success('Table assignment saved');
      await fetchTableData();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to save table assignment');
    }
  };

  const updateColumnFieldRow = (index: number, key: keyof ColumnFieldRow, value: string) => {
    setColumnFields((prev) =>
      prev.map((row, rowIndex) => (rowIndex === index ? { ...row, [key]: value } : row))
    );
  };

  const removeColumnFieldRow = (index: number) => {
    const target = columnFields[index];
    if (target?.field_key?.trim()) {
      const confirmed = window.confirm(
        `Delete field "${target.field_key.trim()}"? This will remove it for all columns once you save.`
      );
      if (!confirmed)
        { 
          return
        };
    }
    setColumnFields((prev) => {
      const next = prev.filter((_, rowIndex) => rowIndex !== index);
      return next.length ? next : [{ field_key: '', field_label: '' }];
    });
  };

  const handleSaveColumnFields = async () => {
    if (!requesterEmail || !columnFieldTableId) {
      toast.error('Select a table first');
      return;
    }
    if (!selectedColumnFieldTable) {
      toast.error('Assign table first before defining column fields');
      return;
    }
    try {
      await consoleTablesApi.updateTableColumnFields({
        requester_email: requesterEmail,
        table_id: Number(columnFieldTableId),
        fields: columnFields
          .map((row) => ({ field_key: row.field_key.trim(), field_label: row.field_label.trim() }))
          .filter((row) => row.field_key.length > 0),
      });
      toast.success('Column field schema saved');
      await fetchTableData();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to save column field schema');
    }
  };

  const handleToggleTableSoftDelete = async (row: ConsoleManagedTable) => {
    if (!requesterEmail) return;
    const nextDeleted = !row.is_deleted;
    const confirmed = window.confirm(
      nextDeleted
        ? `Soft delete table #${row.table_id} (${row.snowflakes_table_name})?`
        : `Restore table #${row.table_id} (${row.snowflakes_table_name})?`
    );
    if (!confirmed) return;

    setTableDeleteLoadingId(row.table_id);
    try {
      await consoleTablesApi.setTableSoftDelete({
        requester_email: requesterEmail,
        table_id: row.table_id,
        is_deleted: nextDeleted,
      });
      toast.success(nextDeleted ? 'Table soft deleted' : 'Table restored');
      await fetchTableData();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to update table delete state');
    } finally {
      setTableDeleteLoadingId(null);
    }
  };

  // ── Dashboard handlers ───────────────────────────────────────────────────────
  const handleToggleDashboardSoftDelete = async (row: ConsoleDashboardSummary) => {
    if (!requesterEmail) return;
    const nextDeleted = !row.is_deleted;
    const confirmed = window.confirm(
      nextDeleted ? `Soft delete dashboard "${row.name}"?` : `Restore dashboard "${row.name}"?`
    );
    if (!confirmed) return;

    setDashboardDeleteLoadingId(row.dashboard_id);
    try {
      await consoleDashboardsApi.setDashboardSoftDelete({
        requester_email: requesterEmail,
        dashboard_id: row.dashboard_id,
        is_deleted: nextDeleted,
      });
      toast.success(nextDeleted ? 'Dashboard soft deleted' : 'Dashboard restored');
      await fetchDashboards();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to update dashboard delete state');
    } finally {
      setDashboardDeleteLoadingId('');
    }
  };

  const handleUpsertDashboard = async (event: FormEvent) => {
    event.preventDefault();
    if (!requesterEmail) return;
    const { form } = dashboardForm;
    if (!form.name.trim()) {
      toast.error('Dashboard name is required');
      return;
    }
    if (!form.primaryDeveloper || !form.secondaryDeveloper) {
      toast.error('Assign both primary and secondary users');
      return;
    }
    try {
      await consoleDashboardsApi.upsertAdminDashboard({
        requester_email: requesterEmail,
        dashboard_id: form.dashboardId || undefined,
        name: form.name.trim(),
        description: form.description.trim(),
        primary_developer_email: form.primaryDeveloper,
        secondary_developer_email: form.secondaryDeveloper,
        source_link: form.sourceLink.trim() || undefined,
        authorized_users_manager_email: form.userManagerAdmin || ''
      });
      toast.success('Dashboard assignment saved');
      dashboardForm.startNew();
      await fetchDashboards();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to save dashboard assignment');
    }
  };

  // ── Access guard ─────────────────────────────────────────────────────────────
  if (!user?.isAdmin) {
    return (
      <ConsoleLayout title="Admin">
        <div className="console-card">
          <p>You do not have access to the Admin section.</p>
        </div>
      </ConsoleLayout>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <ConsoleLayout title="Admin">
      <UserManagementSection
        userEmail={userEmail}
        setUserEmail={setUserEmail}
        firstName={firstName}
        setFirstName={setFirstName}
        lastName={lastName}
        setLastName={setLastName}
        role={role}
        setRole={setRole}
        isActive={isActive}
        setIsActive={setIsActive}
        handleCreateUser={handleCreateUser}
        sortedUsers={sortedUsers}
        loadingUsers={loadingUsers}
        handleToggleUserStatus={handleToggleUserStatus}
      />

      <TableManagementSection
        selectedTableId={selectedTableId}
        setSelectedTableId={setSelectedTableId}
        dailyRefresh={dailyRefresh}
        setDailyRefresh={setDailyRefresh}
        primaryDeveloper={primaryDeveloper}
        setPrimaryDeveloper={setPrimaryDeveloper}
        secondaryDeveloper={secondaryDeveloper}
        setSecondaryDeveloper={setSecondaryDeveloper}
        tableCatalog={tableCatalog}
        activeAssignableUsers={activeAssignableUsers}
        handleAssignTable={handleAssignTable}
        columnFieldTableId={columnFieldTableId}
        setColumnFieldTableId={setColumnFieldTableId}
        columnFields={columnFields}
        setColumnFields={setColumnFields}
        updateColumnFieldRow={updateColumnFieldRow}
        removeColumnFieldRow={removeColumnFieldRow}
        handleSaveColumnFields={handleSaveColumnFields}
        assignedTables={assignedTables}
        loadingTables={loadingTables}
        tableDeleteLoadingId={tableDeleteLoadingId}
        handleToggleTableSoftDelete={handleToggleTableSoftDelete}
      />

      <DashboardManagementSection
        form={dashboardForm.form}
        onFieldChange={dashboardForm.setField}
        onSelectExisting={dashboardForm.selectExisting}
        dashboards={dashboards}
        activeAssignableUsers={activeAssignableUsers}
        loadingDashboards={loadingDashboards}
        dashboardDeleteLoadingId={dashboardDeleteLoadingId}
        handleUpsertDashboard={handleUpsertDashboard}
        loadDashboardIntoForm={dashboardForm.edit}
        handleToggleDashboardSoftDelete={handleToggleDashboardSoftDelete}
        activeDashboardUserManagerAdminUsers={activeDashboardUserManagerAdminUsers} // pre-filtered to tableau_admin role only
      />
    </ConsoleLayout>
  );
};

export default ConsoleAdmin;